import React, { useState } from 'react';
import './Resources.css';
import gdiIrelandLogo from '../Images/logo/GDI-Ireland.png';
import ohdsiIrelandLogo from '../Images/logo/OHDSI_Logo.png';
import candleLogo from '../Images/logo/CANDLE-official-light.svg';
import euLogo from '../Images/logo/eu-flag.svg';
import hiqaLogo from '../Images/logo/HIQA.svg';
import { ExternalLink, Database, GraduationCap, Globe, BookOpen, Building2 } from 'lucide-react';

const resourcesData = [
  {
    id: 1,
    title: 'EU 1+MG Genomic Data Infrastructure – Irish Node',
    description: 'The Irish Node of the European 1+ Million Genomes Initiative (1+MG), providing access to genomic data infrastructure across Europe.',
    url: 'https://genomicdata.ie/',
    icon: 'database',
    logo: gdiIrelandLogo,
    logoAlt: 'Genomic Data Infrastructure Ireland',
    category: 'Data Infrastructure'
  },
  {
    id: 2,
    title: 'OHDSI-Ireland All-Island Node',
    description: 'The Irish national node of the Observational Health Data Sciences and Informatics (OHDSI) network, facilitating collaborative health data research.',
    url: 'https://www.ohdsi-europe.org/index.php/national-nodes/ireland',
    secondaryUrl: '/ohdsi-ireland/seminars',
    secondaryLabel: 'Monthly Seminars',
    icon: 'globe',
    logo: ohdsiIrelandLogo,
    logoAlt: 'OHDSI Ireland',
    category: 'Health Data Network'
  },
  {
    id: 3,
    title: 'CANDLE Project (EU Horizon 2025-2028)',
    description: 'EU Horizon program supporting establishment of National Cancer Data Nodes, aligned with the European Health Data Space (EHDS).',
    url: 'https://candle-project.eu/',
    icon: 'building',
    logo: candleLogo,
    logoAlt: 'CANDLE project',
    category: 'EU Project'
  },
  {
    id: 4,
    title: 'EHDS Ireland – Primary Care',
    description: 'European Health Data Space implementation in Ireland for patient data access through MyHealth@EU and myHealth@myHands initiatives.',
    url: 'https://www.hse.ie/website-maintenance-eh/',
    icon: 'globe',
    logo: euLogo,
    logoAlt: 'European Union',
    category: 'Patient Access'
  },
  {
    id: 5,
    title: 'EHDS Ireland – Secondary Use',
    description: 'HIQA leading the Health Data Access Body (HDAB) and data standards for research through HealthData@EU.',
    url: 'https://www.hiqa.ie/areas-we-work/health-information/healthdataie',
    icon: 'database',
    logo: hiqaLogo,
    logoAlt: 'HIQA',
    category: 'Research Data'
  }
];

const trainingData = [
  {
    id: 1,
    title: 'Introduction to Real World Data in Cancer Clinical Research',
    institution: 'University of Limerick',
    code: 'BM6053',
    url: 'https://www.ul.ie/gps/courses/introduction-to-real-world-data-in-cancer-clinical-research-module-bm6053'
  },
  {
    id: 2,
    title: 'Data Analytics for Cancer Real World Data Research',
    institution: 'University of Limerick',
    code: 'BM6063',
    url: 'https://www.ul.ie/gps/courses/data-analytics-for-cancer-real-world-data-research-module-bm6063'
  },
  {
    id: 3,
    title: 'Real World Data Epidemiology',
    institution: 'University of Oxford (NDORMS)',
    url: 'https://www.ndorms.ox.ac.uk/study/courses/real-world-data-epidemiology'
  },
  {
    id: 4,
    title: 'EHDEN Academy',
    institution: 'European Health Data & Evidence Network',
    description: 'Training for OMOP CDM and OHDSI tools',
    url: 'https://www.ehden.eu/'
  },
  {
    id: 5,
    title: 'SNOMED CT Education',
    institution: 'SNOMED International',
    description: 'Clinical terminology training',
    url: 'https://www.snomed.org/education'
  }
];


function Resources() {
  const [activeSection, setActiveSection] = useState('resources');

  const getIcon = (iconName) => {
    const iconProps = { size: 22, strokeWidth: 1.8, 'aria-hidden': true };
    switch (iconName) {
      case 'database':
        return <Database {...iconProps} />;
      case 'globe':
        return <Globe {...iconProps} />;
      case 'building':
        return <Building2 {...iconProps} />;
      default:
        return <Globe {...iconProps} />;
    }
  };

  return (
    <div className="resources-page">
      <div className="resources-header">
        <h1>Resources & Training</h1>
        <p>Key resources for health data infrastructure, European initiatives, and professional development.</p>

        <div className="tab-buttons">
          <button
            className={`tab-btn ${activeSection === 'resources' ? 'active' : ''}`}
            onClick={() => setActiveSection('resources')}
          >
            <Database size={18} />
            Resources
          </button>
          <button
            className={`tab-btn ${activeSection === 'training' ? 'active' : ''}`}
            onClick={() => setActiveSection('training')}
          >
            <GraduationCap size={18} />
            Training
          </button>

        </div>
      </div>

      <div className="resources-body">
        {/* Resources Section */}
        {activeSection === 'resources' && (
          <div className="resources-grid resources-directory">
            {resourcesData.map(resource => (
              <article key={resource.id} className="directory-resource-card">
                {resource.logo ? (
                  <div className="directory-brand-heading">
                    <img className="resource-brand-logo" src={resource.logo} alt={resource.logoAlt} />
                    <h3>{resource.title}</h3>
                  </div>
                ) : (
                  <>
                    <div className="directory-resource-header">
                      <span className="directory-resource-icon">{getIcon(resource.icon)}</span>
                      <span className="directory-resource-category">{resource.category}</span>
                    </div>
                    <h3>{resource.title}</h3>
                  </>
                )}
                <p>{resource.description}</p>
                <div className="directory-resource-links">
                  <a href={resource.url} target="_blank" rel="noopener noreferrer" className="directory-primary-link">
                    Visit Website <ExternalLink size={14} />
                  </a>
                  {resource.secondaryUrl && (
                    <a href={resource.secondaryUrl} target="_blank" rel="noopener noreferrer" className="directory-secondary-link directory-seminar-link">
                      <BookOpen size={14} /> {resource.secondaryLabel}
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Training Section */}
        {activeSection === 'training' && (
          <div className="training-list">
            {trainingData.map(course => (
              <a
                key={course.id}
                href={course.url}
                target="_blank"
                rel="noopener noreferrer"
                className="training-item"
              >
                <div className="training-icon">
                  <GraduationCap size={24} />
                </div>
                <div className="training-info">
                  <h3>{course.title}</h3>
                  <p className="training-meta">
                    {course.institution}
                    {course.code && <span className="course-code">{course.code}</span>}
                  </p>
                  {course.description && <p className="training-desc">{course.description}</p>}
                </div>
                <ExternalLink size={18} className="training-arrow" />
              </a>
            ))}
          </div>
        )}


      </div>
    </div>
  );
}

export default Resources;
